import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PasswordResetControllerService } from '../api/index.ts';
import './ResetPassword.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [validationError, setValidationError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('Min. 8 znaków');
    if (!/[A-Z]/.test(pwd)) errors.push('Min. 1 wielka litera');
    if (!/[a-z]/.test(pwd)) errors.push('Min. 1 mała litera');
    if (!/[0-9]/.test(pwd)) errors.push('Min. 1 cyfra');
    return errors;
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    setError('');
    if (value) {
      const errors = validatePassword(value);
      setPasswordError(errors.length > 0 ? errors.join(', ') : '');
    } else {
      setPasswordError('');
    }
  };

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValidationError('Brak tokenu resetowania hasła w adresie URL.');
        setIsValidating(false);
        return;
      }

      try {
        await PasswordResetControllerService.validateToken({ token });
        setIsTokenValid(true);
      } catch (err) {
        setValidationError('Link do resetowania hasła jest nieprawidłowy lub wygasł.');
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Wypełnij wszystkie pola');
      return;
    }

    if (passwordError) {
      setError('Hasło nie spełnia wymagań');
      return;
    }

    if (password !== confirmPassword) {
      setError('Hasła nie są zgodne');
      return;
    }

    setIsLoading(true);

    try {
      const response = await PasswordResetControllerService.resetPassword({
        token,
        newPassword: password,
      });
      setSuccessMessage(response.message || 'Hasło zostało zmienione.');
    } catch (err) {
      if (err.status === 400) {
        setError('Hasło nie spełnia wymagań bezpieczeństwa. Sprawdź wymagania i spróbuj ponownie.');
      } else if (err.status === 410 || err.status === 404) {
        setError('Link do resetowania hasła wygasł. Wygeneruj nowy link.');
      } else {
        setError('Wystąpił błąd podczas zmiany hasła. Spróbuj ponownie.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isValidating) {
      return (
        <div className="reset-password-status">
          <div className="spinner"></div>
          <p>Weryfikacja tokenu...</p>
        </div>
      );
    }

    if (validationError) {
      return (
        <div className="reset-password-status">
          <div className="error-message">{validationError}</div>
          <Link to="/forgot-password" className="btn btn-primary btn-reset" style={{ marginTop: '16px', display: 'inline-block', textAlign: 'center' }}>
            Wyślij nowy link
          </Link>
        </div>
      );
    }

    if (successMessage) {
      return (
        <div className="reset-password-status">
          <div className="success-message-box">{successMessage}</div>
          <Link to="/login" className="btn btn-primary btn-reset" style={{ marginTop: '16px', display: 'inline-block', textAlign: 'center' }}>
            Przejdź do logowania
          </Link>
        </div>
      );
    }

    if (isTokenValid) {
      return (
        <form onSubmit={handleSubmit} className="reset-password-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="password">Nowe hasło</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              placeholder="Wprowadź nowe hasło"
              autoComplete="new-password"
              maxLength={128}
              className={passwordError ? 'input-error' : ''}
            />
            <small style={{ display: 'block', marginTop: '3px', color: '#888', fontSize: '0.62rem' }}>
              Min. 8 znaków, 1 wielka litera, 1 mała litera, 1 cyfra
            </small>
            {passwordError && <span className="error-message" style={{ display: 'block', marginTop: '3px', fontSize: '0.62rem' }}>{passwordError}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Potwierdź hasło</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="Powtórz nowe hasło"
              autoComplete="new-password"
              maxLength={128}
              className={password && confirmPassword && password !== confirmPassword ? 'input-error' : ''}
            />
            {password && confirmPassword && password !== confirmPassword && (
              <span className="error-message" style={{ display: 'block', marginTop: '3px', fontSize: '0.62rem' }}>Hasła nie są zgodne</span>
            )}
          </div>

          <button type="submit" className="btn btn-primary btn-reset" disabled={isLoading}>
            {isLoading ? 'Zapisywanie...' : 'Zmień hasło'}
          </button>
        </form>
      );
    }

    return null;
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1>Ustaw nowe hasło</h1>
          <p>Wprowadź nowe hasło do swojego konta</p>
        </div>

        <div className="reset-password-body">
          {renderContent()}
        </div>

        <div className="reset-password-footer">
          <Link to="/login" className="back-to-login-link">
            Powrót do logowania
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
